import { FunctionComponent, useEffect, useState } from 'react'
import { Button, InputCheckbox, InlineAlert } from '../../../../components/BaseComponents'
import CanonicalModal from '../../../../components/BaseComponents/CanonicalModal/CanonicalModal'
import { SortConfig } from '../../../../components/BaseComponents/Datagrid/useSortableData'
import Pagination from '../../../../components/BaseComponents/Pagination/Pagination'
import SimpleSortTable, {
	TableColumn,
} from '../../../../components/BaseComponents/SimpleSortTable/SimpleSortTable'
import { GET_SUPPLIERS_LIMIT } from '../../../../redux/actions/suppliersActions'
import { useAppDispatch } from '../../../../redux/hooks/reduxHooks'
import { SUPPLIERS_RESET } from '../../../../redux/slices/suppliersSlice'
import { FetchStatus } from '../../../../types/LoadingStates'
import { Sector } from '../../../../types/Sector'
import { Supplier } from '../../../../types/Supplier'
import { WORST_SECTOR_LIMIT_QUANTITY } from '../Graphs/ECO2Sector'
import { WORST_SUPPLIER_LIMIT_QUANTITY } from '../Graphs/ECO2Supplier'
import useApi from '../../../../hooks/useApi'
import { useSectorOptions, useSupplierOptions } from './useSelectionLookups'

// This component should only refresh/be doing stuff when 'open' as it lives inside a modal and
// might otherwise be rendered invisibly and cause interference

// Sector endpoint does not have pagination

type IModalSectorSearch = {
	open: boolean
	onClose: () => void
	onChange?: (newValue?: Supplier[]) => void
}

export const ModalSectorToSupplierSearch: FunctionComponent<IModalSectorSearch> = ({
	open,
	onClose,
	onChange,
}) => {
	const dispatch = useAppDispatch()
	const api = useApi();

	const sectors = useSectorOptions(api, open)
	const { current: sectorsCurrent, status: sectorsStatus, error: sectorsError, sectors: allSectors } = sectors

	const [selection, setSelection] = useState<Sector[]>([])
	const [supplierSelection, setSupplierSelection] = useState<boolean>(false)

	const suppliers = useSupplierOptions(api, open)
	const { current: suppliersCurrent, status: suppliersStatus, error: suppliersError, pagination, suppliers: allSuppliers } = suppliers
	const [selectionSuppliers, setSelectionSuppliers] = useState<Supplier[]>([])
	const [orderBy, setOrderBy] = useState<string>('name')
	const [direction, setDirection] = useState<'asc' | 'desc'>('asc')


	// Data loading (refire every time the modal opens)
	useEffect(() => {
		if (open) {
			setSelection([])
			setSelectionSuppliers([])
			setSupplierSelection(false)
			setOrderBy('name')
			setDirection('asc')
			dispatch(SUPPLIERS_RESET())
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open, api])

	// DataGrid Columns
	const columns: TableColumn[] = [
		{
			id: 'selected',
			label: '',
			columnClass: 'w-3', // narrow fixed column width
			renderer: (columnKey, rowData) => {
				return (
					<InputCheckbox
						className='rounded border-charcoal-300 text-algae-600'
						value={rowData._id}
						checked={rowData.selected}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							event.stopPropagation()

							if (selection.filter((sector) => sector.code === rowData._id).length > 0) {
								// Remove from selection
								setSelection([...selection.filter((sector) => sector.code !== rowData._id)])
							} else {
								// Check there's still room to add another
								if (selection.length !== WORST_SECTOR_LIMIT_QUANTITY) {
									// Add to selected
									setSelection([...selection, rowData.sectorObject])
								}
							}
						}}
					/>
				)
			},
		},
		{
			id: 'isicCode',
			label: 'Code',
			renderer: (columnKey, rowData) => {
				return <p className='text-white'>{rowData._id}</p>
			},
		},
		{ id: 'name', label: 'Description' },
	]

	// DataGrid data constructor
	const constructData = (dataSet: Sector[]) => [
		...dataSet.map((sector) => {
			return {
				_id: sector.code,
				name: sector.name,
				selected:
					selection.filter((selectedSupplier) => selectedSupplier.code === sector.code).length > 0,
				sectorObject: sector,
			}
		}),
	]
	const getSuppliersList = () => {
		suppliers.load({ sectorCodes: selection.map((sector) => sector.code).join(',') })
	}
	// DataGrid Columns
	const columnsSupplier: TableColumn[] = [
		{
			id: 'selected',
			label: '',
			sortable: false,
			columnClass: 'w-3', // narrow fixed column width
			renderer: (columnKey, rowData) => {
				return (
					<InputCheckbox
						className='rounded border-charcoal-300 text-algae-600'
						value={rowData._id}
						checked={rowData.selected}
						onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
							event.stopPropagation()

							if (selectionSuppliers.filter((supplier) => supplier.id === rowData._id).length > 0) {
								// Remove from selection
								setSelectionSuppliers([...selectionSuppliers.filter((supplier) => supplier.id !== rowData._id)])
							} else {
								// Check there's still room to add another
								if (selectionSuppliers.length !== WORST_SUPPLIER_LIMIT_QUANTITY) {
									// Add to selected
									setSelectionSuppliers([...selectionSuppliers, rowData.supplierObject])
								}
							}
						}}
					/>
				)
			},
		},
		{ id: 'name', label: 'Supplier', sortable: true },
		{
			id: 'isicCode',
			label: 'Sector Code (ISIC)',
			sortable: false,
			columnClass: 'col-span-2',
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>
							{rowData.isicCode}
							{rowData.isicCodeName && (
								<span className='text-charcoal-050'> - {rowData.isicCodeName}</span>
							)}
						</p>
					</>
				)
			},
		},
		{
			id: 'jurisdiction',
			label: 'Jurisdiction',
			sortable: false,
			renderer: (columnKey, rowData) => {
				return (
					<>
						<p className='text-white'>{rowData.jurisdiction_code}</p>
					</>
				)
			},
		},
	]
	const defaultSorting: SortConfig = {
		key: 'name',
		direction: 'ascending',
	}

	// DataGrid data constructor
	const constructDataSupplier = (dataSet: Array<Supplier>) => [
		...dataSet.map((supplier) => {
			return {
				_id: supplier.id,
				name: supplier.name,
				isicCode: supplier.supplierIndustryCode?.code || '-',
				isicCodeName: supplier.supplierIndustryCode?.name || undefined,
				jurisdiction_code: supplier.jurisdiction_code || '-',
				selected:
					selectionSuppliers.filter((selectedSupplier) => selectedSupplier.id === supplier.id).length > 0,
				supplierObject: supplier,
			}
		}),
	]

	const actions = supplierSelection
		? selectionSuppliers.length > 0
			? (
				<Button
					type='Primary'
					text='Save selection'
					disabled={!suppliersCurrent || suppliersStatus !== FetchStatus.loaded}
					onClick={() => onChange?.(selectionSuppliers)}
				/>
			)
			: null
		: selection.length > 0
			? (
				<Button
					type='Primary'
					text='Fetch suppliers'
					disabled={!sectorsCurrent || sectorsStatus !== FetchStatus.loaded}
					onClick={() => {
						getSuppliersList()
						setSupplierSelection(true)
					}}
				/>
			)
			: null

	return (
		<CanonicalModal
			open={open}
			onClose={onClose}
			size='small'
			title={supplierSelection ? 'Select suppliers to compare' : 'Select sectors to compare'}
			actions={actions}
		>
			{!supplierSelection && <>
				{sectorsCurrent && sectorsStatus === FetchStatus.error && (
					<InlineAlert type='error' className='mt-4'>						<p className='text-white'>
							There was an error fetching sectors associated with this organisation. If this problem
							persists, please contact the Neoni team.
						</p>
						<p className='text-white'>{sectorsError}</p>
						<Button type='Primary' text='Retry' onClick={sectors.retry} />
					</InlineAlert>
				)}

				<p className='text-white mb-4'>
					Please select up to {WORST_SECTOR_LIMIT_QUANTITY} sectors to compare their embedded CO₂
					emissions:
				</p>

				{sectorsCurrent && selection.length > 0 && (
					<>
						{selection.map((sector) => {
							return (
								<Button
									key={sector.code}
									type='Outlined'
									size='sm'
									additionalClass='mr-2 mb-2'
									text={`${sector.code} - ${sector.name}`}
									hasTrailingIcon
									icon='fa-solid fa-xmark'
									onClick={() => {
										// Remove from selection
										setSelection([
											...selection.filter((selectedSector) => selectedSector.code !== sector.code),
										])
									}}
								/>
							)
						})}
					</>
				)}

				{sectorsCurrent && sectorsStatus !== FetchStatus.error && <SimpleSortTable
					isLoading={sectorsStatus === FetchStatus.loading}
					additionalClass='mt-4'
					data={constructData(allSectors)}
					columns={columns}
				/>}

			</>}
			{supplierSelection && <>
				{suppliersCurrent && suppliersStatus === FetchStatus.error && (
					<InlineAlert type='error' className='mt-4'>						<p className='text-white'>
							There was an error fetching suppliers associated with this organisation. If this problem
							persists, please contact the Neoni team.
						</p>
						<p className='text-white'>{suppliersError}</p>
						<Button type='Primary' text='Retry' onClick={suppliers.retry} />
					</InlineAlert>
				)}

				<p className='text-white mb-4'>
					Please select up to {WORST_SUPPLIER_LIMIT_QUANTITY} suppliers to compare their embedded CO₂
					emissions:
				</p>

				{suppliersCurrent && selectionSuppliers.length > 0 && (
					<>
						{selectionSuppliers.map((supplier) => {
							return (
								<Button
									key={supplier.id}
									type='Outlined'
									size='sm'
									additionalClass='mr-2 mb-2'
									text={supplier.name}
									hasTrailingIcon
									icon='fa-solid fa-xmark'
									onClick={() => {
										// Remove from selectionSuppliers
										setSelectionSuppliers([
											...selectionSuppliers.filter((selectedSupplier) => selectedSupplier.id !== supplier.id),
										])
									}}
								/>
							)
						})}
					</>
				)}
				{suppliersCurrent && suppliersStatus !== FetchStatus.error && <><SimpleSortTable
					isLoading={suppliersStatus === FetchStatus.loading}
					additionalClass='mt-4'
					data={constructDataSupplier(allSuppliers)}
					columns={columnsSupplier}
					defaultSorting={defaultSorting}
					onSortPress={(key, direction) => {
						// Store current sorting values for pagination to reference in its API calls
						setOrderBy(key)
						setDirection(direction === 'ascending' ? 'asc' : 'desc')
						suppliers.load({
							...suppliers.requested, page: pagination.current_page, limit: GET_SUPPLIERS_LIMIT,
							orderBy: key, direction: direction === 'ascending' ? 'asc' : 'desc',
						})
					}}
				/>

				<Pagination
					isLoading={suppliersStatus === FetchStatus.loading}
					pagination={pagination}
					onPageChange={(newPageNumber) => {
						suppliers.load({
							...suppliers.requested, page: newPageNumber, limit: GET_SUPPLIERS_LIMIT, orderBy, direction,
						})
					}}
				/></>}

			</>}
		</CanonicalModal>
	)
}
